import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";

export default function InterestForm() {
  const [form, setForm] = useState({
    discord_username: "",
    email: "",
    favorite_games: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.discord_username.trim()) {
      toast.error("Discord username is required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/interest", {
        ...form,
        email: form.email || null,
      });
      setSuccess(true);
      toast.success("You're on the list, soldier.");
      setForm({ discord_username: "", email: "", favorite_games: "", message: "" });
    } catch (e) {
      toast.error("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="join"
      data-testid="interest-section"
      className="relative max-w-7xl mx-auto px-6 md:px-12 py-28"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline mb-3">// 04 · ENLIST</div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight">
            Want the bot in <span className="text-brand-orange">your server?</span>
          </h2>
          <p className="text-zinc-400 mt-5 leading-relaxed">
            Drop your Discord handle and we'll reach out when we open access. No
            spam, no fluff — just an invite to the squad and early access to
            new features.
          </p>
          <div className="mt-8 space-y-3 text-sm font-mono-tabular text-zinc-500">
            <div>▸ Free for community servers under 500 members</div>
            <div>▸ Direct line to the dev team for feature requests</div>
            <div>▸ Beta access to upcoming Gemini 3.1 Pro modules</div>
          </div>
        </div>

        <div className="md:col-span-7">
          <form
            onSubmit={onSubmit}
            className="tactical-card p-8 md:p-10 clip-corner"
            data-testid="interest-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="overline block mb-2">Discord Username *</label>
                <input
                  className="tactical-input"
                  placeholder="e.g. ShadowReaper#1337"
                  value={form.discord_username}
                  onChange={(e) => setForm({ ...form, discord_username: e.target.value })}
                  data-testid="interest-discord-input"
                  required
                />
              </div>
              <div>
                <label className="overline block mb-2">Email (optional)</label>
                <input
                  className="tactical-input"
                  type="email"
                  placeholder="contact@606.gg"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="interest-email-input"
                />
              </div>
            </div>

            <div className="mt-5">
              <label className="overline block mb-2">Favorite games</label>
              <input
                className="tactical-input"
                placeholder="Valorant, CoD, Apex…"
                value={form.favorite_games}
                onChange={(e) => setForm({ ...form, favorite_games: e.target.value })}
                data-testid="interest-games-input"
              />
            </div>

            <div className="mt-5">
              <label className="overline block mb-2">Message</label>
              <textarea
                className="tactical-input min-h-[110px] resize-none"
                placeholder="Tell us about your community…"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="interest-message-input"
              />
            </div>

            <div className="mt-6 flex items-center justify-between gap-4">
              <div className="font-mono-tabular text-xs text-zinc-500">
                {success && (
                  <span className="text-emerald-400 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> SUBMISSION RECEIVED
                  </span>
                )}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
                data-testid="interest-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Application
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
